/**
 * Statements page (§13.3).
 *
 * "all statements with parse status; the review queue (§8.4, plus
 *  alert/reconciliation items) front and center when non-empty."
 *
 * Rebuilt for scale. The first version listed everything in one flat table,
 * which was fine for three statements and useless at forty-seven across eight
 * cards. What people actually want to do here is narrow: *this card*, *this
 * month*, *the ones that need me*. So the page is now filter-first —
 * card chips, a search box, and a needs-checking toggle — with a running count
 * so you always know how much you're looking at versus how much exists.
 */

import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, FileText, RefreshCw, Search, X } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { CardFilterBar } from "@/components/statements/CardFilterBar";
import { ReviewDialog } from "@/components/statements/ReviewDialog";
import { UnlockPrompt } from "@/components/statements/UnlockPrompt";
import { UploadDropzone } from "@/components/statements/UploadDropzone";
import { SheetContent } from "@/components/ui/Sheet";
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Input,
  Panel,
  Skeleton, PageHeader } from "@/components/ui/primitives";
import { cardsApi, statementsApi, type ReviewItem, type Statement } from "@/lib/api/client";
import { cn } from "@/lib/cn";
import { formatDate, formatMoney } from "@/lib/format";
import { useIsPhone } from "@/lib/useMedia";

type SortKey = "date-desc" | "date-asc" | "amount-desc";

export function StatementsPage() {
  const qc = useQueryClient();
  const [reviewing, setReviewing] = useState<Statement | null>(null);
  const [detail, setDetail] = useState<Statement | null>(null);
  const phone = useIsPhone();
  const [cardId, setCardId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  const [sort, setSort] = useState<SortKey>("date-desc");

  const { data: statements, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["statements"],
    queryFn: () => statementsApi.list(),
  });
  const { data: cards } = useQuery({ queryKey: ["cards", true], queryFn: () => cardsApi.list(true) });
  const { data: queue } = useQuery({ queryKey: ["review-queue"], queryFn: statementsApi.reviewQueue });

  const reparse = useMutation({
    mutationFn: (id: string) => statementsApi.reparse(id),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["statements"] });
      qc.invalidateQueries({ queryKey: ["review-queue"] });
      toast.success(r.message);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const cardById = useMemo(
    () => new Map((cards ?? []).map((c) => [c.id, c])),
    [cards],
  );
  const cardName = (id: string) => {
    const c = cardById.get(id);
    return c ? c.nickname || `${c.issuer} ····${c.last4}` : "Unknown card";
  };

  const all = statements ?? [];

  const visible = useMemo(() => {
    let rows = all;
    if (cardId) rows = rows.filter((s) => s.card_id === cardId);
    if (onlyFlagged) rows = rows.filter((s) => s.needs_review);
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((s) => {
        const name = cardName(s.card_id).toLowerCase();
        return (
          name.includes(q) ||
          (s.statement_date ?? "").includes(q) ||
          (s.total_amount_due ?? "").includes(q) ||
          formatDate(s.statement_date).toLowerCase().includes(q)
        );
      });
    }
    const sorted = [...rows];
    sorted.sort((a, b) => {
      if (sort === "amount-desc") {
        return Number(b.total_amount_due ?? 0) - Number(a.total_amount_due ?? 0);
      }
      const cmp = (a.statement_date ?? "").localeCompare(b.statement_date ?? "");
      return sort === "date-asc" ? cmp : -cmp;
    });
    return sorted;
  }, [all, cardId, onlyFlagged, search, sort, cardById]);

  const needsReview = all.filter((s) => s.needs_review);

  // A statement that needs review also raises a queue item, so counting both
  // double-counts. Only surface queue items that AREN'T already a row below —
  // a locked PDF or an unknown card never became a statement at all.
  const statementIds = new Set(all.map((s) => s.id));
  const orphanItems =
    queue?.filter((item) => {
      const id = item.payload?.statement_id;
      return !(typeof id === "string" && statementIds.has(id));
    }) ?? [];

  const totalToCheck = needsReview.length + orphanItems.length;
  const filtered = visible.length !== all.length;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={
          all.length > 0
            ? `${all.length} statement${all.length === 1 ? "" : "s"} · ${cardById.size} card${cardById.size === 1 ? "" : "s"}`
            : "The source of truth"
        }
        title="Statements"
        description="Every number in MyDeck traces back to one of these. Anything MyDeck wasn't sure of waits here for you."
      />

      {/* Phones: the review queue first, then upload (mobile M3). Desktop
          keeps upload first. */}
      {!phone && <UploadDropzone />}

      {/* Review queue — front and centre when non-empty (§13.3) */}
      {totalToCheck > 0 && (
        <Panel className="border-warn/30 bg-warn/[0.04] p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warn" />
            <div className="min-w-0 flex-1 space-y-1">
              <h2 className="font-display text-sm font-semibold text-ink">
                {totalToCheck === 1 ? "1 thing needs" : `${totalToCheck} things need`} you
              </h2>
              {needsReview.length > 0 && (
                <p className="text-sm text-muted">
                  {needsReview.length === 1
                    ? "1 statement was read but not with full confidence"
                    : `${needsReview.length} statements were read but not with full confidence`}{" "}
                  — nothing was guessed. Check against the PDF.
                </p>
              )}
              {orphanItems.length > 0 && <ReviewGroups items={orphanItems} />}
            </div>
            {needsReview.length > 0 && (
              <Button variant="default" size="sm" onClick={() => setReviewing(needsReview[0]!)}>
                Review
              </Button>
            )}
          </div>
        </Panel>
      )}

      {phone && <UploadDropzone />}

      {isError ? (
        <Panel>
          <ErrorState message={(error as Error).message} onRetry={() => refetch()} />
        </Panel>
      ) : isLoading ? (
        <Panel className="space-y-2 p-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </Panel>
      ) : !all.length ? (
        <Panel>
          <EmptyState
            icon={FileText}
            title="No statements yet"
            body="Drop a PDF above. MyDeck needs the card's password saved first — add it in Cards if you haven't."
          />
        </Panel>
      ) : (
        <>
          {/* --- Filters ------------------------------------------------- */}
          <div className="space-y-3">
            <CardFilterBar
              cards={cards ?? []}
              statements={all}
              selected={cardId}
              onSelect={setCardId}
            />

            <div className="flex flex-wrap items-center gap-2">
              <div className="relative min-w-[12rem] flex-1 sm:max-w-xs">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-faint" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by date or amount"
                  className="h-8 pl-8 pr-7 text-xs"
                  aria-label="Search statements"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    aria-label="Clear search"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-faint hover:text-ink"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              <Button
                variant={onlyFlagged ? "default" : "ghost"}
                size="sm"
                aria-pressed={onlyFlagged}
                onClick={() => setOnlyFlagged((v) => !v)}
                className={cn(onlyFlagged && "border-warn/40 text-warn")}
              >
                Needs checking
                {needsReview.length > 0 && (
                  <span className="tabular-nums">({needsReview.length})</span>
                )}
              </Button>

              <select
                value={sort}
                onChange={(e) => setSort(e.target.value as SortKey)}
                aria-label="Sort statements"
                className="h-8 rounded-lg border border-hairline bg-base px-2 text-xs text-muted focus:border-brass/50 focus:outline-none"
              >
                <option value="date-desc">Newest first</option>
                <option value="date-asc">Oldest first</option>
                <option value="amount-desc">Largest amount</option>
              </select>

              {filtered && (
                <span className="text-xs text-faint">
                  {visible.length} of {all.length}
                  <button
                    type="button"
                    onClick={() => {
                      setCardId(null);
                      setSearch("");
                      setOnlyFlagged(false);
                    }}
                    className="ml-2 text-brass underline-offset-2 hover:underline"
                  >
                    Clear
                  </button>
                </span>
              )}
            </div>
          </div>

          {/* --- Table --------------------------------------------------- */}
          {visible.length === 0 ? (
            <Panel>
              <EmptyState
                icon={Search}
                title="Nothing matches"
                body="No statements match these filters. Try a different card, or clear the search."
                action={
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => {
                      setCardId(null);
                      setSearch("");
                      setOnlyFlagged(false);
                    }}
                  >
                    Clear filters
                  </Button>
                }
              />
            </Panel>
          ) : (
            phone ? (
              <Panel className="overflow-hidden">
                <ul>
                  {visible.map((s) => {
                    const card = cardById.get(s.card_id);
                    return (
                      <li key={s.id} className="border-b border-hairline last:border-0">
                        <button
                          type="button"
                          onClick={() => (s.needs_review ? setReviewing(s) : setDetail(s))}
                          className="flex min-h-16 w-full items-center gap-3 px-4 py-3 text-left active:bg-raised"
                        >
                          <span
                            aria-hidden
                            className="h-9 w-1 shrink-0 rounded-full bg-hairline-strong"
                            style={
                              card?.theme
                                ? { background: `linear-gradient(${card.theme.gradient_from}, ${card.theme.gradient_to})` }
                                : undefined
                            }
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-[15px] text-ink">{cardName(s.card_id)}</span>
                            <span className="mt-0.5 block text-xs text-faint">
                              {formatDate(s.statement_date)} · {s.transaction_count} txns
                            </span>
                          </span>
                          <span className="flex shrink-0 flex-col items-end gap-1">
                            <span className="font-medium tabular-nums text-ink">{formatMoney(s.total_amount_due)}</span>
                            <StatusBadge statement={s} />
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </Panel>
            ) : (
            <Panel className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[46rem] text-sm">
                  <thead>
                    <tr className="border-b border-hairline">
                      {["Card", "Statement", "Due", "Total due", "Txns", "Status", ""].map((h) => (
                        <th key={h} className="label-micro px-4 py-2.5 text-left font-medium">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((s) => {
                      const card = cardById.get(s.card_id);
                      return (
                        <tr
                          key={s.id}
                          className="border-b border-hairline last:border-0 hover:bg-raised/40"
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {card?.theme && (
                                <span
                                  aria-hidden
                                  className="h-6 w-1 shrink-0 rounded-full"
                                  style={{
                                    background: `linear-gradient(${card.theme.gradient_from}, ${card.theme.gradient_to})`,
                                  }}
                                />
                              )}
                              <div className="min-w-0">
                                <div className="truncate text-ink">{cardName(s.card_id)}</div>
                                {card && (
                                  <div className="text-micro text-faint">····{card.last4}</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-muted">{formatDate(s.statement_date)}</td>
                          <td className="px-4 py-3 text-muted">
                            <DueCell statement={s} />
                          </td>
                          <td className="px-4 py-3 font-medium text-ink">
                            {formatMoney(s.total_amount_due)}
                          </td>
                          <td className="px-4 py-3 tabular-nums text-muted">
                            {s.transaction_count}
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge statement={s} />
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end gap-1">
                              {s.needs_review && (
                                <Button variant="ghost" size="sm" onClick={() => setReviewing(s)}>
                                  Check
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Re-parse this statement"
                                aria-label="Re-parse this statement"
                                disabled={reparse.isPending}
                                onClick={() => reparse.mutate(s.id)}
                              >
                                <RefreshCw className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Panel>
            )
          )}
        </>
      )}

      <StatementSheet
        statement={detail}
        cardName={detail ? cardName(detail.card_id) : ""}
        onOpenChange={(o) => !o && setDetail(null)}
        onReparse={(id) => reparse.mutate(id)}
        reparsing={reparse.isPending}
      />

      {reviewing && (
        <ReviewDialog
          statement={reviewing}
          cards={cards ?? []}
          open
          onOpenChange={(o) => !o && setReviewing(null)}
        />
      )}
    </div>
  );
}

/** The order groups appear in: things only the user can unblock first. */
const GROUP_ORDER: ReviewItem["type"][] = [
  "password_needed",
  "no_attachment",
  "unknown_card",
  "parse_failed",
  "alert_parse_failed",
  "unreconciled_alert",
  "unknown_sender",
];

/**
 * The review queue, one group per kind of problem.
 *
 * It used to be one row per item, each repeating the same full sentence — the
 * first production sync rendered that sentence twelve times for twelve
 * unmapped senders, and the page read as a wall of errors. The instruction is
 * now said once per group, and each item is a single line of what identifies
 * it, with a way to dismiss it once the user has checked it.
 */
function ReviewGroups({ items }: { items: ReviewItem[] }) {
  const groups = GROUP_ORDER.map((type) => ({
    type,
    items: items.filter((i) => i.type === type),
  })).filter((g) => g.items.length > 0);
  const rest = items.filter((i) => !GROUP_ORDER.includes(i.type));
  if (rest.length) groups.push({ type: rest[0]!.type, items: rest });

  return (
    <div className="space-y-3 pt-2">
      {groups.map((g) =>
        g.type === "password_needed" ? (
          <ul key={g.type} className="space-y-2">
            {g.items.map((item) => (
              <li key={item.id}>
                <UnlockPrompt item={item} />
              </li>
            ))}
          </ul>
        ) : (
          <section key={g.type} className="space-y-1">
            <p className="text-xs text-warn">
              {describeReviewGroup(g.type)}
              {g.items.length > 1 && (
                <span className="tabular-nums text-faint"> · {g.items.length}</span>
              )}
            </p>
            <ul className="space-y-0.5">
              {g.items.map((item) => (
                <ReviewLine key={item.id} item={item} />
              ))}
            </ul>
          </section>
        ),
      )}
    </div>
  );
}

/**
 * One item: what identifies it, and a dismiss button.
 *
 * The identifying detail is not decoration — an unknown-sender row that doesn't
 * say *which* address can't be acted on, and a queue item you can't act on is
 * just anxiety (§8.4).
 */
function ReviewLine({ item }: { item: ReviewItem }) {
  const qc = useQueryClient();
  const dismiss = useMutation({
    mutationFn: () => statementsApi.dismissReview(item.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["review-queue"] }),
    onError: (e: Error) => toast.error(e.message),
  });

  const payload = (item.payload ?? {}) as Record<string, unknown>;
  const text = (v: unknown) => (typeof v === "string" && v.length > 0 ? v : null);
  const parts = [text(payload.filename), text(payload.from), text(payload.subject)].filter(
    (v): v is string => v !== null,
  );

  return (
    <li className="group flex items-center gap-2 text-xs">
      <p className="min-w-0 flex-1 truncate text-faint" title={parts.join(" — ")}>
        {parts.length ? (
          <>
            <span className="font-mono">{parts[0]}</span>
            {parts.slice(1).map((p) => (
              <span key={p}> — {p}</span>
            ))}
          </>
        ) : (
          "No details recorded"
        )}
      </p>
      <button
        type="button"
        onClick={() => dismiss.mutate()}
        disabled={dismiss.isPending}
        className="shrink-0 rounded p-0.5 text-faint opacity-60 transition hover:bg-ink/10 hover:text-ink hover:opacity-100 focus-visible:opacity-100 disabled:opacity-30"
        aria-label={`Dismiss ${parts[0] ?? "this item"}`}
        title="Dismiss — checked, nothing to do"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </li>
  );
}

/**
 * Review-item kinds in the user's words, not the enum's (§13.1 copy), said
 * once per group. Each one says what to do.
 */
function describeReviewGroup(type: ReviewItem["type"]): string {
  switch (type) {
    case "unknown_card":
      return "Statements for a card MyDeck doesn't know yet — add the card, then upload again.";
    case "parse_failed":
      return "PDFs that couldn't be read as a card statement — dismiss any that aren't one, or map the sender as Other in Settings.";
    case "no_attachment":
      return "Statement emails with no PDF attached — download from your bank and drop it above.";
    case "unknown_sender":
      return "Possible card mail from addresses not mapped to a bank — map them in Settings.";
    case "unreconciled_alert":
      return "Transaction alerts that never matched a statement line.";
    case "alert_parse_failed":
      return "Transaction alerts that couldn't be read.";
    default:
      return "Needs your attention.";
  }
}

/** A statement where nothing was owed. The balance is the reliable signal —
 *  some issuers print a due date on a nil bill, others print "Nil" instead. */
function isSettled(s: Statement): boolean {
  return s.total_amount_due !== null && Number(s.total_amount_due) === 0;
}

/**
 * The Due column.
 *
 * A blank cell reads as missing data — as if MyDeck failed to find something.
 * When nothing was owed, that's a fact about the statement, not a gap, so it
 * says so. A printed due date is always shown even on a nil bill: the document
 * is the source of truth (§2.2) and hiding what it says would be its own kind
 * of lie.
 */
function DueCell({ statement }: { statement: Statement }) {
  if (statement.payment_due_date) {
    return (
      <span className={cn(isSettled(statement) && "text-faint")}>
        {formatDate(statement.payment_due_date)}
      </span>
    );
  }
  if (isSettled(statement)) {
    return (
      <span
        className="text-faint"
        title="Nothing was owed, so this statement carries no payment due date."
      >
        None needed
      </span>
    );
  }
  // Money is owed but no due date was read — that IS a gap, and the statement
  // will already be flagged for review.
  return (
    <span className="text-warn" title="No due date could be read from this statement.">
      Not found
    </span>
  );
}

function StatusBadge({ statement }: { statement: Statement }) {
  if (statement.needs_review) return <Badge tone="warn">Check</Badge>;
  if (statement.parse_status === "failed") return <Badge tone="overdue">Failed</Badge>;
  // Settled outranks the parse status here: "nothing to pay" is what you want
  // to know at a glance, and it's true regardless of how the file was read.
  if (isSettled(statement)) {
    return (
      <Badge tone="paid" title="Nothing was owed on this statement.">
        Settled
      </Badge>
    );
  }
  if (statement.parse_status === "manual") return <Badge tone="neutral">By hand</Badge>;
  return <Badge tone="paid">Read</Badge>;
}


/** One statement, in full — the phone's tap-a-row sheet (mobile M3). A row
 *  that needs checking opens the review sheet instead. */
function StatementSheet({
  statement: s,
  cardName,
  onOpenChange,
  onReparse,
  reparsing,
}: {
  statement: Statement | null;
  cardName: string;
  onOpenChange: (open: boolean) => void;
  onReparse: (id: string) => void;
  reparsing: boolean;
}) {
  return (
    <Dialog.Root open={s !== null} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <SheetContent className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-[min(30rem,94vw)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto">
          {s && (
            <div className="space-y-5 px-5 pb-6 pt-3">
              <div>
                <Dialog.Title className="font-display text-lg font-semibold text-ink">{cardName}</Dialog.Title>
                <Dialog.Description className="mt-1 text-xs text-faint">
                  Statement of {formatDate(s.statement_date)}
                </Dialog.Description>
              </div>
              <p className="font-display text-[34px] font-semibold tabular-nums tracking-[-0.03em] text-brass">
                {formatMoney(s.total_amount_due)}
              </p>
              <dl className="divide-y divide-hairline rounded-xl border border-hairline text-sm">
                <div className="flex justify-between gap-4 px-3.5 py-2.5">
                  <dt className="text-faint">Due</dt>
                  <dd className="text-right text-ink">
                    <DueCell statement={s} />
                  </dd>
                </div>
                <div className="flex justify-between gap-4 px-3.5 py-2.5">
                  <dt className="text-faint">Transactions</dt>
                  <dd className="tabular-nums text-ink">{s.transaction_count}</dd>
                </div>
                <div className="flex items-center justify-between gap-4 px-3.5 py-2.5">
                  <dt className="text-faint">Status</dt>
                  <dd>
                    <StatusBadge statement={s} />
                  </dd>
                </div>
              </dl>
              <Button variant="default" className="w-full" disabled={reparsing} onClick={() => onReparse(s.id)}>
                <RefreshCw className="h-4 w-4" aria-hidden />
                Read this statement again
              </Button>
            </div>
          )}
        </SheetContent>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
